import { useEffect, useState } from 'react'
import { RecoveryFlowState, type RecoveryFlow } from '@ory/client'
import { safeRead, safeRemove, safeWrite } from '@utils/session-storage.utils'

// Recovery code-resend cooldown storage. Two flags per flow id:
//   - cooldownEndsAt.<flowId>: epoch ms — when the user may resend again
//   - resendPending.<flowId>:  '1' iff a resend was just initiated; consumed
//                              by the post-303 remount to refresh the cooldown
//
// Scoped by flow id so a fresh recovery flow (e.g. "back to login" → restart
// with a different email) doesn't inherit the cooldown from a previous one
// in the same tab. sessionStorage clears on tab close, matching the lifetime
// we want.

const COOLDOWN_KEY_PREFIX = 'paretos.authui.recovery.cooldownEndsAt.'
const PENDING_KEY_PREFIX = 'paretos.authui.recovery.resendPending.'

// 60s mirrors Kratos's default per-identity recovery resend throttle
// (configurable via courier.message_retries / selfservice.methods.code.config
// — neither is set in our kratos.yaml, so the upstream default applies).
// Keep this in sync with the server limit; setting it lower exposes the user
// to a Kratos 429 they can't see, higher just makes them wait pointlessly.
const RESEND_COOLDOWN_MS = 60_000

const cooldownKey = (flowId: string): string => `${COOLDOWN_KEY_PREFIX}${flowId}`
const pendingKey = (flowId: string): string => `${PENDING_KEY_PREFIX}${flowId}`

export const readCooldownEndsAt = (flowId: string): number => {
  const raw = safeRead(cooldownKey(flowId))
  const n = raw === null ? 0 : Number(raw)
  return Number.isFinite(n) ? n : 0
}

const writeCooldownEndsAt = (flowId: string, ts: number): void => {
  safeWrite(cooldownKey(flowId), String(ts))
}

// Single write path so RESEND_COOLDOWN_MS stays private to this module.
export const startResendCooldown = (flowId: string): void => {
  writeCooldownEndsAt(flowId, Date.now() + RESEND_COOLDOWN_MS)
}

// Click handler doesn't write storage cooldown (that's the post-303
// remount's job) but still needs to lock React state immediately to block
// a double-click between handler and form submit. Returns the seed value
// in seconds so the local countdown lines up with what tick will write
// once the post-303 cooldown lands.
export const optimisticCooldownSeconds = (): number => RESEND_COOLDOWN_MS / 1000

// The pending flag is the only signal across click → form-submit → Kratos
// 303 → remount that "the user just initiated a resend, not a refresh".
// Writing the cooldown to storage in the click handler instead would lock
// the user out for 60s even when the network drops before Kratos sees the
// request — this defers that write to the post-303 mount.
export const markResendPending = (flowId: string): void => {
  safeWrite(pendingKey(flowId), '1')
}

const isResendPending = (flowId: string): boolean => safeRead(pendingKey(flowId)) === '1'

// Pure read for the tick guard — consumes belong to the seed effect, which
// runs only on a real post-303 mount. The tick uses the peek to tell
// "natural cooldown expiration" (write 0) apart from "click handler just
// fired, optimistic state in flight" (preserve state).
export const peekResendPending = isResendPending

export const consumeResendPending = (flowId: string): boolean => {
  if (!isResendPending(flowId)) {
    return false
  }
  safeRemove(pendingKey(flowId))
  return true
}

interface CooldownApi {
  resendRemaining: number
  // Wire to the resend form's onSubmit. Marks the post-303 remount as a
  // confirmed resend and locks local state to block a double-click in the
  // ~ms gap before the browser commits form navigation.
  handleResendSubmit: () => void
}

export const useResendCooldown = (flow: RecoveryFlow | null): CooldownApi => {
  const [resendRemaining, setResendRemaining] = useState(0)

  // Hoisted so deps can stay exhaustive without pulling the whole `flow`
  // object in — a re-fetch producing a new object with the same id+state
  // should not re-seed cooldown or restart the interval.
  const flowId = flow?.id
  const flowState = flow?.state

  // Seed cooldown on first entry to code stage (or after the previous
  // window elapsed), refresh it on a mount that follows a confirmed resend
  // (pending flag set). Refreshing strictly on the post-303 mount means a
  // network drop between click and Kratos leaves the previous cooldown
  // intact instead of locking the user out for an attempt that never
  // reached the server.
  useEffect(() => {
    if (!flowId || flowState !== RecoveryFlowState.SentEmail) {
      return
    }
    if (consumeResendPending(flowId)) {
      startResendCooldown(flowId)
      return
    }
    if (readCooldownEndsAt(flowId) <= Date.now()) {
      startResendCooldown(flowId)
    }
  }, [flowId, flowState])

  // Tick once per second from sessionStorage so a page reload mid-cooldown
  // resumes where it left off. The peek-pending branch handles the brief
  // window between handleResendSubmit's optimistic state set and the
  // post-303 remount writing fresh cooldown: without it, a tick firing in
  // that window would clobber the optimistic 60 with 0 from stale storage
  // and briefly re-enable the button on slow networks.
  useEffect(() => {
    if (!flowId) {
      return undefined
    }
    const tick = (): void => {
      const endsAt = readCooldownEndsAt(flowId)
      if (endsAt <= Date.now()) {
        if (peekResendPending(flowId)) {
          return
        }
        setResendRemaining(0)
        return
      }
      setResendRemaining(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)))
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => {
      clearInterval(id)
    }
  }, [flowId])

  const handleResendSubmit = (): void => {
    if (!flow) {
      return
    }
    markResendPending(flow.id)
    // Mirror the cooldown duration so the displayed countdown continues
    // smoothly once the post-303 remount writes fresh storage and tick
    // re-syncs, rather than jumping from some lower value.
    setResendRemaining(optimisticCooldownSeconds())
  }

  return { resendRemaining, handleResendSubmit }
}
